"use client";

import { useCallback, useState } from "react";
import { toast } from "sonner";

import { addCartItem, applyCartPoints } from "../api";
import {
  selectCartItems,
  selectCartVariantIdsSet,
  useAddToCartMutation,
  cartApi,
  useGetCartQuery,
  useLazyGetCartQuery,
  useRemoveFromCartMutation,
  useUpdateCartItemMutation,
} from "../cartApi";
import type { CartPointsUsageResponseApi, NormalizedCart } from "../types";
import { useGetProfileQuery } from "@/src/features/profile/profileApi";
import { useAppSelector } from "@/src/store/hooks";

type CartMutationState =
  | {
      type: "add";
      variantId: number;
    }
  | {
      type: "update";
      itemId: number;
      quantity: number;
    }
  | {
      type: "remove";
      itemId: number;
    }
  | null;

function getCartErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";

  if (message.includes("Out of stock")) {
    return "عذرًا، لا يمكن زيادة الكمية لأنك وصلت إلى أقصى كمية متاحة من هذا المنتج في المخزون.";
  }

  if (message.includes("Item not found")) {
    return "قطعة الأثاث المطلوبة غير موجودة في عربة التسوق";
  }

  if (message.includes("Authentication credentials")) {
    return "تعذر الوصول إلى عربة التسوق حاليًا";
  }

  return "حدث خطأ، حاول مرة أخرى";
}

function getPointsErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";

  if (message.includes("Points exceed balance")) {
    return "لا يمكنك استخدام تذاكر خصم أكثر من رصيدك الحالي";
  }

  if (message.includes("Invalid points")) {
    return "أدخل عدد تذاكر خصم صالح";
  }

  if (message.includes("No wallet")) {
    return "لا يوجد رصيد تذاكر خصم مرتبط بهذا الحساب";
  }

  if (message.includes("Authentication credentials")) {
    return "سجّل دخولك أولًا لاستخدام التذاكر";
  }

  return "تعذر تطبيق التذاكر الآن";
}

export function useCart(options?: { autoLoad?: boolean }) {
  const autoLoad = options?.autoLoad ?? true;
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const cartItems = useAppSelector(selectCartItems);
  const cartVariantIdsSet = useAppSelector(selectCartVariantIdsSet);
  const cartState = cartApi.endpoints.getCart.useQueryState(undefined);
  const {
    data: queriedCart,
    error: queriedCartError,
    isFetching: isCartFetching,
    isLoading: isCartLoading,
    refetch,
  } = useGetCartQuery(undefined, {
    skip: !autoLoad,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [triggerGetCart] = useLazyGetCartQuery();
  const [addToCart] = useAddToCartMutation();
  const [removeFromCart] = useRemoveFromCartMutation();
  const [updateCartItemMutation] = useUpdateCartItemMutation();
  const cart = (queriedCart ?? cartState.data ?? null) as NormalizedCart | null;
  const { data: profile } = useGetProfileQuery(undefined, {
    skip: !accessToken || cart?.type !== "user",
  });
  const availablePoints = profile?.points ?? null;
  const [pointsPreview, setPointsPreview] =
    useState<CartPointsUsageResponseApi | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [isApplyingPoints, setIsApplyingPoints] = useState(false);
  const [mutationState, setMutationState] = useState<CartMutationState>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const resolveCart = useCallback(async () => {
    if (cart) {
      return cart;
    }

    const result = await triggerGetCart(undefined, true);

    if ("data" in result && result.data) {
      return result.data;
    }

    throw result.error ?? new Error("Failed to load cart");
  }, [cart, triggerGetCart]);

  const reloadCart = useCallback(async () => {
    setMutationError(null);

    if (autoLoad) {
      await refetch();
      return;
    }

    await triggerGetCart(undefined);
  }, [autoLoad, refetch, triggerGetCart]);

  const addItem = useCallback(
    async (variantId: number) => {
      setIsMutating(true);
      setMutationState({ type: "add", variantId });
      setMutationError(null);

      try {
        await addToCart({ variantId }).unwrap();
        // The add mutation writes a temporary cache item so the badge updates
        // immediately. Replace it with the canonical cart before callers (and
        // product-details redirects) continue to the cart page.
        await triggerGetCart(undefined, false).unwrap();
        setPointsPreview(null);
        toast.success("تمت إضافة قطعة الأثاث إلى عربة التسوق");
      } catch (mutationError) {
        setMutationError(getCartErrorMessage(mutationError));
        toast.error(getCartErrorMessage(mutationError));
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [addToCart, triggerGetCart],
  );

  const addItems = useCallback(
    async (variantIds: number[]) => {
      const uniqueVariantIds = Array.from(new Set(variantIds)).filter(
        (variantId) => Number.isInteger(variantId) && variantId > 0,
      );

      if (uniqueVariantIds.length === 0) {
        toast.error("اختار قطعة واحدة على الأقل");
        return false;
      }

      setIsMutating(true);
      setMutationState({ type: "add", variantId: uniqueVariantIds[0] });
      setMutationError(null);

      try {
        const baseCart = await resolveCart();
        const existingVariantIds = new Set(
          baseCart.items.map((item) => item.variantId),
        );
        const idsToAdd = uniqueVariantIds.filter(
          (variantId) => !existingVariantIds.has(variantId),
        );

        if (idsToAdd.length === 0) {
          toast.success("الاختيارات المحددة موجودة بالفعل في عربة التسوق");
          return true;
        }

        for (const idToAdd of idsToAdd) {
          await addCartItem(idToAdd, accessToken);
        }

        await triggerGetCart(undefined, false).unwrap();
        setPointsPreview(null);
        toast.success(
          idsToAdd.length === 1
            ? "تمت إضافة قطعة الأثاث إلى عربة التسوق"
            : "تمت إضافة الاختيارات المحددة إلى عربة التسوق",
        );

        return true;
      } catch (mutationError) {
        setMutationError(getCartErrorMessage(mutationError));
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, resolveCart, triggerGetCart],
  );

  const updateItemQuantity = useCallback(
    async (itemId: number, quantity: number, variantId?: number) => {
      setIsMutating(true);
      setMutationState({ type: "update", itemId, quantity });
      setMutationError(null);

      try {
        await updateCartItemMutation({ itemId, quantity, variantId, isGuest: cart?.type === "guest" }).unwrap();
        setPointsPreview(null);
        toast.success("تم تحديث كمية قطعة الأثاث");
      } catch (mutationError) {
        setMutationError(getCartErrorMessage(mutationError));
        toast.error(getCartErrorMessage(mutationError));
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [cart, updateCartItemMutation],
  );

  const removeItem = useCallback(
    async (itemId: number, variantId?: number) => {
      setIsMutating(true);
      setMutationState({ type: "remove", itemId });
      setMutationError(null);

      try {
        await removeFromCart({ itemId, variantId, isGuest: cart?.type === "guest" }).unwrap();
        setPointsPreview(null);
        toast.success("تم حذف قطعة الأثاث من عربة التسوق");
      } catch (mutationError) {
        setMutationError(getCartErrorMessage(mutationError));
        toast.error(getCartErrorMessage(mutationError));
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [cart, removeFromCart],
  );

  const applyPoints = useCallback(
    async (points: number) => {
      if (!Number.isInteger(points) || points < 0) {
        toast.error("أدخل عدد تذاكر خصم صالح");
        return;
      }

      if (availablePoints !== null && points > availablePoints) {
        toast.error("لا يمكنك استخدام تذاكر خصم أكثر من رصيدك الحالي");
        return;
      }

      setIsApplyingPoints(true);

      try {
        const nextPointsPreview = await applyCartPoints(points, accessToken);
        setPointsPreview(nextPointsPreview);
        toast.success("تم تحديث خصم التذاكر");
      } catch (pointsError) {
        toast.error(getPointsErrorMessage(pointsError));
      } finally {
        setIsApplyingPoints(false);
      }
    },
    [accessToken, availablePoints],
  );

  const clearPointsPreview = useCallback(() => {
    setPointsPreview(null);
  }, []);

  const toggleItemByVariant = useCallback(
    async (variantId: number) => {
      setIsMutating(true);
      setMutationError(null);

      try {
        const baseCart = await resolveCart();
        const existingItem = baseCart.items.find(
          (item) => item.variantId === variantId,
        );

        if (existingItem) {
          setMutationState({ type: "remove", itemId: existingItem.itemId });
          await removeFromCart({ itemId: existingItem.itemId }).unwrap();
        } else {
          setMutationState({ type: "add", variantId });
          await addToCart({ variantId }).unwrap();
          // Do not leave the optimistic "updating product" row in RTK Query's
          // cache. The next screen must receive the real title, price and media.
          await triggerGetCart(undefined, false).unwrap();
        }

        setPointsPreview(null);
        toast.success(
          existingItem
            ? "تم حذف قطعة الأثاث من عربة التسوق"
            : "تمت إضافة قطعة الأثاث إلى عربة التسوق",
        );
        return !existingItem;
      } catch (mutationError) {
        setMutationError(getCartErrorMessage(mutationError));
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [addToCart, removeFromCart, resolveCart, triggerGetCart],
  );

  const isInCart = useCallback(
    (variantId: number | null) =>
      typeof variantId === "number" && cartVariantIdsSet.has(variantId),
    [cartVariantIdsSet],
  );

  const error = mutationError
    ? mutationError
    : queriedCartError || cartState.error
      ? getCartErrorMessage(queriedCartError ?? cartState.error)
      : null;
  const isLoading = autoLoad && (isCartLoading || isCartFetching) && !cart;

  return {
    cart,
    cartItems,
    availablePoints,
    pointsPreview,
    isLoading,
    isMutating,
    isApplyingPoints,
    mutationState,
    error,
    isAuthenticated,
    cartVariantIdsSet,
    isInCart,
    reloadCart,
    addItem,
    addItems,
    toggleItemByVariant,
    updateItemQuantity,
    removeItem,
    applyPoints,
    clearPointsPreview,
  };
}
